import { useMemo } from 'react';

import dayjs from 'dayjs';
import { RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DataPlaneMerchant, EndType, NumberCustomField, TextCustomField, WidgetInputs } from '../@types/types';
import { useFeature } from '../featureFlags';
import {
	GiftType,
	dateFieldOptions,
	dateInputFormat,
	defaultFieldOptions,
	frequencies,
	givingAmountMin,
	regexValidationLettersOnly,
	regexValidationNumbersOnly,
} from '../shared/constants';
import { isCustomTextField, isCustomNumberField, isLettersOnlyInput, getNextFirstOrFifteenth } from '../shared/utils';

const FIRST_OR_FIFTEENTH_DATES_ARRAY = [1, 15];

export function useFormFieldOptions({
	config,
	currentSelection,
}: {
	config: DataPlaneMerchant;
	currentSelection: {
		endType: EndType;
		isOneTime: boolean;
		isRecurring: boolean;
		giftType: GiftType;
		recurringStartDate?: string;
		recurringEndDate?: string;
		recurrence?: string;
	};
}): {
	fieldOptions: Partial<Record<keyof WidgetInputs, RegisterOptions<WidgetInputs>>>;
	customFieldKeys: Record<string, string>;
} {
	const { t: translate } = useTranslation();
	const allowMultiFunds = useFeature('MultiFunds');
	const {
		MerchantSettings: { PaymentMin, PaymentMax, OneTimeFutureGiftEnabled, SupportsMultiFundGiving },
		CustomFields,
		FundCustomLabel = '',
	} = config;

	const multiFundsEnabled = allowMultiFunds && SupportsMultiFundGiving;
	const { recurringStartDate, recurrence, giftType, endType, isRecurring } = currentSelection;

	const fundFieldOption = useMemo(
		() =>
			multiFundsEnabled
				? {
						required: {
							value: true,
							message: translate(!FundCustomLabel ? 'errors.fundRequired' : 'errors.namedFundRequired', {
								label: FundCustomLabel,
							}),
						},
				  }
				: { ...defaultFieldOptions.FundKeyOrName }, // FeatureFlag(MultiFunds) will make FundKeyOrName on defaultFieldOptions obsolete

		[FundCustomLabel, multiFundsEnabled, translate]
	);

	const { customFieldOptions, customFieldKeys } = useMemo(
		() =>
			CustomFields.reduce(
				(acc, field) => {
					const opts = {
						required: field.IsRequired,
						setValueAs: (value: any) => (typeof value === 'string' ? value.trim() : value),
					} as RegisterOptions<any>;
					if (field.Type === isCustomTextField) {
						opts.maxLength = (field as TextCustomField).MaxLength;
						opts.minLength = (field as TextCustomField).MinLength;
						if (isLettersOnlyInput((field as TextCustomField).Validation.toString())) {
							opts.pattern = {
								value: new RegExp(regexValidationLettersOnly),
								message: translate('errors.lettersOnly'),
							};
						}
					}
					if (field.Type === isCustomNumberField) {
						opts.maxLength = (field as NumberCustomField).MaxLength;
						opts.minLength = (field as NumberCustomField).MinLength;
						opts.min = 0;
						opts.pattern = {
							value: new RegExp(regexValidationNumbersOnly),
							message: translate('errors.minNumericValue'),
						};
					}
					acc.customFieldOptions[field.Key] = opts;
					acc.customFieldKeys[field.Key] = `f[${field.Order}]`;

					return acc;
				},
				{
					customFieldOptions: {},
					customFieldKeys: {},
				} as {
					customFieldOptions: Record<string, RegisterOptions<any>>;
					customFieldKeys: Record<string, string>;
				}
			),
		[CustomFields, translate]
	);

	const endDateMin = useMemo(() => {
		const start = dayjs(recurringStartDate || dateFieldOptions.min).startOf('day');
		const startDay = dayjs(recurringStartDate || dateFieldOptions.min).date();

		const isValidStartDay = FIRST_OR_FIFTEENTH_DATES_ARRAY.includes(startDay);
		if (!isValidStartDay && frequencies.FirstAndFifteenth.name === recurrence) {
			const firstPaymentDate = getNextFirstOrFifteenth(dayjs(recurringStartDate).format(dateInputFormat));
			const min = dayjs(firstPaymentDate).add(1, 'day').format(dateInputFormat);
			return { value: min, message: 'errors.endsBeforeFirstAndFifteenth' };
		}

		const minEndDay = dayjs(start).add(1, 'day').format(dateInputFormat);
		return { value: minEndDay, message: 'errors.endAfterStart' };
	}, [recurrence, recurringStartDate]);

	return useMemo(
		() => ({
			fieldOptions: {
				...{
					Amount: {
						...defaultFieldOptions.Amount,
						min: Math.max(givingAmountMin, PaymentMin ?? givingAmountMin),
						max: PaymentMax,
					},
					GiftType: { required: true },
					FundKeyOrName: fundFieldOption,
					GiftDate: {
						required:
							OneTimeFutureGiftEnabled &&
							giftType === GiftType.ONE_TIME &&
							giftType === GiftType.ONE_TIME,
					},
					RecurringStartDate: {
						required: isRecurring,
						min: dateFieldOptions.min,
					},
					Recurrence: { required: isRecurring },
					RecurringEndOccurence: {
						required: isRecurring && endType === EndType.Occurrences,
						...defaultFieldOptions.RecurringEndOccurence,
					},
					RecurringEndDate: {
						required: isRecurring && endType === EndType.Date,
						min: endDateMin,
					},
					RecurringEndType: {
						required: isRecurring && endType === EndType.Date,
					},
				},
				...customFieldOptions,
			},
			customFieldKeys,
		}),
		[
			endDateMin,
			PaymentMin,
			PaymentMax,
			OneTimeFutureGiftEnabled,
			giftType,
			isRecurring,
			endType,
			customFieldOptions,
			customFieldKeys,
			fundFieldOption,
		]
	);
}
